import { pickChatFile } from '@euler/app/flows/chat/components/composer/attachmentActions';
import { AiAttachmentInfo, AiAttachmentMimeType } from '@euler/model/apdm';
import { useServiceFactory } from '@euler/services/factory';
import { FilePlus, Paperclip, Replace, Upload } from 'lucide-react-native';
import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Pressable, Text } from 'react-native';
import { useTheme } from 'styled-components';
import { ReviewAction } from './ReviewControls';
import {
  NativeAttachmentUploadAppearance,
  NativeAttachmentUploadIcon,
} from './nativeAttachmentUpload';

const kIcons = {
  paperclip: Paperclip,
  file_plus: FilePlus,
  replace: Replace,
  upload: Upload,
} as const;

export function ApdmUploadButton({
  disabled,
  onUploaded,
  mode = 'history',
  appearance = 'button',
  label = '上传 Excel / CSV',
  icon = 'paperclip',
}: {
  disabled?: boolean;
  onUploaded: (info: AiAttachmentInfo) => Promise<void>;
  mode?: 'history' | 'image';
  appearance?: NativeAttachmentUploadAppearance;
  /** The chip or row's words, in place of 上传 Excel / CSV. */
  label?: string;
  icon?: NativeAttachmentUploadIcon;
}) {
  const Icon = kIcons[icon];
  const { aiAttachmentService } = useServiceFactory();
  const theme = useTheme();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = useRef(true);
  const working = useRef(false);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const upload = async () => {
    if (!active.current || working.current || disabled) return;
    setError(null);
    // Nothing is uploading while the user is still choosing a file: the
    // button stays as it is, and a cancelled picker leaves it usable.
    const selected = await pickChatFile();
    if (!selected) return;
    if (!active.current || working.current) {
      URL.revokeObjectURL(selected.uri);
      return;
    }
    working.current = true;
    const objectUrl = selected.uri;
    try {
      if (
        !(mode === 'image' ? /\.(jpg|jpeg|png)$/i : /\.(xls|xlsx|csv)$/i).test(
          selected.filename,
        )
      ) {
        setError(
          mode === 'image'
            ? '请选择 JPEG 或 PNG 图片。'
            : '请选择 XLS、XLSX 或 CSV 服务历史文件。',
        );
        return;
      }
      setUploading(true);
      const body = new FormData();
      const blob = await (await fetch(selected.uri)).blob();
      if (!active.current) return;
      body.append('file', blob, selected.filename);
      const info = await aiAttachmentService.upload(body);
      if (!active.current) return;
      if (
        mode === 'image' &&
        ![AiAttachmentMimeType.Jpeg, AiAttachmentMimeType.Png].includes(
          info.mimeType,
        )
      )
        throw new Error('Unsupported image attachment');
      await onUploaded(info);
    } catch {
      if (active.current) setError('上传未完成，请检查文件后重试。');
    } finally {
      URL.revokeObjectURL(objectUrl);
      working.current = false;
      if (active.current) setUploading(false);
    }
  };
  if (appearance === 'primary')
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={label}
        disabled={disabled || uploading}
        onPress={() => {
          void upload();
        }}
        style={({ pressed }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          height: 42,
          borderRadius: 11,
          backgroundColor: '#1A6FD8',
          opacity: disabled ? 0.45 : pressed ? 0.85 : 1,
        })}
      >
        {uploading ? (
          <ActivityIndicator size="small" color="#fff" />
        ) : (
          <Icon size={16} color="#fff" />
        )}
        <Text style={{ fontSize: 14, fontWeight: '500', color: '#fff' }}>
          {uploading ? '正在上传…' : error ? '上传失败，重试' : label}
        </Text>
      </Pressable>
    );
  if (appearance !== 'button')
    return (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="上传 Excel 或 CSV"
        disabled={disabled || uploading}
        onPress={() => {
          void upload();
        }}
        style={
          appearance === 'chip' || appearance === 'row'
            ? {
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                height: 32,
                paddingHorizontal: 10,
                borderWidth: 1,
                borderColor: '#E2E8F0',
                borderRadius: 9,
                backgroundColor: '#fff',
                opacity: disabled ? 0.5 : 1,
              }
            : {
                width: 36,
                height: 36,
                alignItems: 'center',
                justifyContent: 'center',
                opacity: disabled ? 0.5 : 1,
              }
        }
      >
        {uploading ? (
          <ActivityIndicator size="small" color="#1A6FD8" />
        ) : (
          <Icon
            size={appearance === 'icon' ? 20 : 16}
            color={appearance === 'icon' ? '#64748B' : '#1A6FD8'}
          />
        )}
        {appearance !== 'icon' && (
          <Text style={{ fontSize: 13, color: error ? '#DC2626' : '#0F172A' }}>
            {error ? '上传失败，重试' : label}
          </Text>
        )}
      </Pressable>
    );
  return (
    <>
      <ReviewAction
        title={mode === 'image' ? '选择 JPEG／PNG 图片' : '选择服务历史文件'}
        loading={uploading}
        disabled={disabled}
        onPress={() => {
          void upload();
        }}
      />
      {error && (
        <Text
          accessibilityRole="alert"
          style={{ color: theme.colors.status.danger }}
        >
          {error}
        </Text>
      )}
    </>
  );
}
